import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Remembers scroll position per history entry so the back/forward buttons
// return the user to exactly where they were, while any fresh navigation
// (nav bar link, footer link, card click, etc.) lands at the top of the
// new page — standard SPA scroll behaviour.
const scrollPositions = new Map();

export default function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const outgoingRef = useRef(location.key);

  // Runs during the RENDER phase, i.e. strictly before React commits the
  // DOM mutations for the new route. window.scrollY here still reflects the
  // OUTGOING page (the browser hasn't swapped/clamped anything yet), so
  // this is the only safe place to capture its true scroll position —
  // reading it later (e.g. in an effect cleanup) happens after the DOM has
  // already been swapped and the browser may have auto-clamped scrollY to
  // the new, often-shorter page's height.
  if (outgoingRef.current !== location.key) {
    scrollPositions.set(outgoingRef.current, window.scrollY);
    outgoingRef.current = location.key;
  }

  useEffect(() => {
    const target = navigationType === "POP" ? scrollPositions.get(location.key) : undefined;

    if (!target) {
      // Fresh navigation (PUSH/REPLACE) or nothing remembered for this
      // history entry — start at the top.
      window.scrollTo(0, 0);
      return;
    }

    // The destination page (Home, Jobs, etc.) often fetches its content
    // asynchronously and mounts at a short "skeleton" height first, so an
    // immediate window.scrollTo(0, target) gets silently clamped by the
    // browser to whatever little height exists at that instant. Keep
    // re-applying the target for a short window as the real content loads
    // and the page grows, until it "sticks" or the window expires.
    const RESTORE_WINDOW_MS = 2000;
    const start = performance.now();
    const maxScroll = () => Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    let rafId;

    const tick = () => {
      if (maxScroll() >= target && Math.abs(window.scrollY - target) > 2) {
        window.scrollTo(0, target);
      }
      if (performance.now() - start < RESTORE_WINDOW_MS) {
        rafId = requestAnimationFrame(tick);
      }
    };
    window.scrollTo(0, target);
    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
  }, [location.key, navigationType]);

  return null;
}
